import { Box, Typography, Chip, LinearProgress } from '@mui/material'
import { styled } from '@mui/material/styles'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { Link as RouterLink } from 'react-router-dom'
import LazyImage from '../lazy-image'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import { useMediaState } from '../../context/media-state-context'
import { dateOptions } from '../../utils/date-options'
import { parseUtcDate } from '../../utils/parse-utc-date'
import { showProgressIndicators } from '../../utils/progress-visibility'
import { useAuth } from '../../context/AuthContext'
import { ReusableTypes } from '../../types/ReusableTypes'

interface LargeCardProps {
  id: number
  type: ReusableTypes['MovieShowPerson']
  title: string
  year?: string
  rating?: number
  posterUrl?: string
  genres?: string[]
  tmdbId?: number
  lastWatchedOnUtc?: string
  episodesWatchedCount?: number
  episodesTotal?: number
  progressNote?: string
  // Own-profile showcase pinning; the toggle renders only when provided.
  pinned?: boolean
  onTogglePin?: () => void
}

// A real anchor so right-click / middle-click / cmd+click open in a new tab.
const StyledCardWrapper = styled(RouterLink)(({ theme }) => ({
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  width: 220,
  flexShrink: 0,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  transition: 'transform 0.3s ease',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'scale(1.03)',
      '& .card-poster': {
        borderColor: 'transparent',
        backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(135deg, #8B1E3F 0%, #8B1E3F 50%, #4A0E1F 100%)`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      },
      '& .card-overlay': {
        opacity: 1,
      },
    },
  },
}))

const StyledPoster = styled(Box)(({ theme }) => ({
  width: 220,
  height: 330,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
  position: 'relative',
  border: '2px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.5s ease',
  backgroundColor: theme.palette.background.default,
}))

const StyledPosterFallback = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 64,
  backgroundColor: theme.palette.background.default,
}))

const StyledOverlay = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background:
    'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 60%, transparent 100%)',
  padding: 16,
  opacity: 0,
  transition: 'opacity 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const IconRow = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(0.5),
  zIndex: 2,
  background: 'rgba(0,0,0,0.35)',
  borderRadius: 12,
  padding: '4px 6px',
}))

const StyledGenreWrapper = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
})

const StyledGenreChip = styled(Chip)({
  height: 20,
  fontSize: 10,
  backgroundColor: 'rgba(255,255,255,0.15)',
  color: '#fff',
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
  },
})

const StyledOverlayText = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontSize: 13,
}))

const StyledInfoWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 15,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const StyledMetaWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}))

const StyledSecondaryText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
}))

interface StyledRatingProps {
  value: number
}

const StyledRating = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'value',
})<StyledRatingProps>(({ theme, value }) => {
  let color = theme.palette.green.main

  if (value < 4) {
    color = theme.palette.error.main
  } else if (value < 7) {
    color = theme.palette.warning.main
  }

  return {
    color,
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  }
})

// Poster action toggles: white by default, colored once the item is in the
// matching list. Watchlist locks (dimmed, unclickable) once the title is watched.
const WatchedToggle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  cursor: 'pointer',
  display: 'flex',
  color: active ? '#2e7d32' : '#fff',
}))

const FavoriteToggle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  cursor: 'pointer',
  display: 'flex',
  color: active ? '#e53935' : '#fff',
}))

const WatchlistToggle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'locked',
})<{ active: boolean; locked: boolean }>(({ active, locked }) => ({
  cursor: locked ? 'not-allowed' : 'pointer',
  display: 'flex',
  color: active ? '#fbc02d' : '#fff',
  opacity: locked ? 0.4 : 1,
  pointerEvents: locked ? 'none' : 'auto',
}))

const PinToggle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  cursor: 'pointer',
  display: 'flex',
  color: active ? theme.palette.brand.main : '#fff',
}))

const EpisodeProgressWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
}))

const ProgressNoteText = styled(StyledSecondaryText)(({ theme }) => ({
  fontSize: 11,
  marginTop: theme.spacing(0.25),
}))

const CardProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  marginTop: theme.spacing(0.5),
}))

const LargeCard = ({
  id,
  type,
  title,
  year,
  rating,
  posterUrl,
  genres = [],
  tmdbId,
  lastWatchedOnUtc,
  episodesWatchedCount,
  episodesTotal,
  progressNote,
  pinned,
  onTogglePin,
}: LargeCardProps) => {
  const mediaId = tmdbId || id
  const { isAuthenticated } = useAuth()
  const hasMediaActions = type !== 'person' && isAuthenticated
  const {
    favoritesSet,
    watchlistSet,
    historySet,
    toggleFavorite,
    toggleWatchlist,
    toggleHistory,
  } = useMediaState()

  const meta = {
    title,
    posterPath: posterUrl,
    releaseYear: year ? Number(year) : undefined,
    genres: genres ?? [],
  }

  return (
    <StyledCardWrapper
      to={type === 'person' ? `/actors/${id}` : `/details/${type}/${id}`}
    >
      <StyledPoster className='card-poster'>
        {hasMediaActions && (
          <IconRow>
            <WatchedToggle
              active={historySet.has(mediaId)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleHistory(
                  mediaId,
                  type === 'movie' ? 'movie' : 'show',
                  meta,
                )
              }}
            >
              {historySet.has(mediaId) ? (
                <AccessTimeFilledIcon fontSize='small' />
              ) : (
                <AccessTimeOutlinedIcon fontSize='small' />
              )}
            </WatchedToggle>
            <FavoriteToggle
              active={favoritesSet.has(mediaId)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleFavorite(
                  mediaId,
                  type === 'movie' ? 'movie' : 'show',
                  meta,
                )
              }}
            >
              {favoritesSet.has(mediaId) ? (
                <FavoriteIcon fontSize='small' />
              ) : (
                <FavoriteBorderIcon fontSize='small' />
              )}
            </FavoriteToggle>
            <WatchlistToggle
              active={watchlistSet.has(mediaId)}
              locked={historySet.has(mediaId)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleWatchlist(
                  mediaId,
                  type === 'movie' ? 'movie' : 'show',
                  meta,
                )
              }}
            >
              {watchlistSet.has(mediaId) ? (
                <PlaylistAddCheckRoundedIcon fontSize='small' />
              ) : (
                <PlaylistAddRoundedIcon fontSize='small' />
              )}
            </WatchlistToggle>
            {onTogglePin && (
              <PinToggle
                active={!!pinned}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onTogglePin()
                }}
              >
                {pinned ? (
                  <PushPinIcon fontSize='small' />
                ) : (
                  <PushPinOutlinedIcon fontSize='small' />
                )}
              </PinToggle>
            )}
          </IconRow>
        )}

        <LazyImage
          src={posterUrl}
          alt={title}
          fallback={
            <StyledPosterFallback>
              {title.slice(0, 1).toUpperCase() || '?'}
            </StyledPosterFallback>
          }
        />

        <StyledOverlay className='card-overlay'>
          {genres.length > 0 ? (
            <StyledGenreWrapper>
              {genres.slice(0, 3).map((genre) => (
                <StyledGenreChip key={genre} label={genre} size='small' />
              ))}
            </StyledGenreWrapper>
          ) : null}
          <StyledOverlayText>View Details {'>'}</StyledOverlayText>
        </StyledOverlay>
      </StyledPoster>

      <StyledInfoWrapper>
        <StyledTitle>{title}</StyledTitle>
        <StyledMetaWrapper>
          <StyledSecondaryText>{year}</StyledSecondaryText>
          {rating ? (
            <StyledRating value={rating}>
              <StarRoundedIcon fontSize='inherit' />
              {rating.toFixed(1)}
            </StyledRating>
          ) : null}
        </StyledMetaWrapper>
        {lastWatchedOnUtc ? (
          <StyledSecondaryText>
            Last Watched on{' '}
            {parseUtcDate(lastWatchedOnUtc).toLocaleDateString(
              undefined,
              dateOptions,
            )}
          </StyledSecondaryText>
        ) : null}
        {episodesWatchedCount && showProgressIndicators() ? (
          <EpisodeProgressWrapper>
            <StyledSecondaryText>
              Episodes watched: {episodesWatchedCount}
              {episodesTotal ? ` / ${episodesTotal}` : ''}
              {episodesTotal && episodesTotal > 0
                ? ` (${Math.floor(
                    (episodesWatchedCount / episodesTotal) * 100,
                  )}%)`
                : ''}
            </StyledSecondaryText>
            {progressNote ? (
              <ProgressNoteText>{progressNote}</ProgressNoteText>
            ) : null}
            {episodesTotal && episodesTotal > 0 ? (
              <CardProgressBar
                variant='determinate'
                value={Math.min(
                  100,
                  (episodesWatchedCount / episodesTotal) * 100,
                )}
              />
            ) : null}
          </EpisodeProgressWrapper>
        ) : null}
      </StyledInfoWrapper>
    </StyledCardWrapper>
  )
}

export default LargeCard
